import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { colorThemeGenerator, ButtonPrimary, Type } from '@spotify-internal/encore-creator-web';
import { Link } from 'react-router-dom';
import tokens from '@spotify-internal/encore-foundation';
import { formatDisplayTimestamp } from 'lib/Formatter';
import { MetricsWidgetWithRouter } from 'components/Shared/MetricsWidget';
import { ResizableText } from 'components/Shared/ResizableText';
import { MeContext } from '../../../context/me';
import { FormattedDateParts, FormattedMessage } from 'react-intl';

export class Header extends PureComponent {
  static propTypes = {
    artworkUrl: PropTypes.string,
    publisher: PropTypes.string,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    metrics: PropTypes.shape({
      starts: PropTypes.number,
      streams: PropTypes.number,
      listeners: PropTypes.number,
      followers: PropTypes.number,
    }).isRequired,
    metadata: PropTypes.shape({
      releaseDate: PropTypes.string,
      duration: PropTypes.number,
      numEpisodes: PropTypes.number,
    }),
    navigation: PropTypes.shape({
      network: PropTypes.object,
      podcast: PropTypes.object,
    }),
    toggleTrailerSelectionModal: PropTypes.func,
    trailer: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      artworkUrl: PropTypes.string.isRequired,
    }),
  };

  static contextType = MeContext;

  renderArtwork() {
    return (
      <div>
        <Artwork alt="artwork" src={this.props.artworkUrl} />
      </div>
    );
  }

  renderAttribution() {
    const {
      navigation: { network = {}, podcast },
      publisher,
    } = this.props;
    if (publisher && !network.name)
      return (
        <StyledType condensed color="gray60" variant={Type.body1}>
          <FormattedMessage
            id={'header.attribution.publisher'}
            description={'attribution to use if only publisher name available'}
            defaultMessage={'By {publisher}'}
            values={{
              publisher,
            }}
          />
        </StyledType>
      );

    if (podcast && !network.name)
      return (
        <StyledType condensed color="gray60" variant={Type.body1}>
          <FormattedMessage
            id="header.attribution.podcast"
            description="Attribution to use if only podcast name available and is a link"
            defaultMessage={`By <link>{podcastName}</link>`}
            values={{
              podcastName: podcast.name,
              link: (...chunks) => <Link to={`/podcast/${podcast.id}`}>{chunks}</Link>,
            }}
          />
        </StyledType>
      );

    if (network && podcast)
      return (
        <StyledType condensed color="gray60" variant={Type.body1}>
          <FormattedMessage
            id="header.attribution.podcast-network"
            description="Attribution to use if podcast and network names are available and are links"
            defaultMessage={`By <link1>{podcastName}</link1> from <link2>{networkName}</link2>`}
            values={{
              podcastName: podcast.name,
              networkName: network.name,
              link1: (...chunks) => <Link to={`/podcast/${podcast.id}`}>{chunks}</Link>,
              link2: (...chunks) => <Link to={`/network/${network.id}`}>{chunks}</Link>,
            }}
          />
        </StyledType>
      );

    return (
      <StyledType condensed color="gray60" variant={Type.body1}>
        <FormattedMessage
          id="header.attribution.network"
          description="Attribution to use if only network name available and is a link"
          defaultMessage={`From <link>{networkName}</link>`}
          values={{
            networkName: network.name,
            link: (...chunks) => <Link to={`/network/${network.id}`}>{chunks}</Link>,
          }}
        />
      </StyledType>
    );
  }

  renderMetrics() {
    const { metrics, type } = this.props;
    return (
      <MetricsWidgetWithRouter
        data-qa={`${type}-metrics`}
        metadata={metrics}
        metricsKeys={Object.keys(metrics)}
      />
    );
  }

  renderMetadata() {
    const { metadata } = this.props;

    if (metadata.numEpisodes)
      return (
        <div>
          <StyledType condensed color="gray60" variant={Type.body1}>
            <FormattedMessage
              id="header.attribution.episode-count"
              defaultMessage="{episodeCount, plural,
                one {# Episode}
                other {# Episodes}
              }"
              values={{ episodeCount: metadata.numEpisodes }}
            />
          </StyledType>
        </div>
      );

    const [year, month, day] = metadata.releaseDate
      ? metadata.releaseDate.split('-')
      : [null, null, null];

    return (
      <GridContainerMetadata>
        <StyledType condensed color="gray60" variant={Type.body1}>
          {metadata.releaseDate && (
            <FormattedDateParts
              value={new Date(...[year, month - 1, day])}
              year="numeric"
              month="short"
              day="numeric"
            >
              {parts => (
                <>
                  {parts[2].value}
                  {parts[1].value}
                  {parts[0].value}
                  {parts[1].value}
                  {parts[4].value}
                </>
              )}
            </FormattedDateParts>
          )}
        </StyledType>
        <Type condensed color="gray60" variant={Type.cta3}>
          •
        </Type>
        <StyledType condensed color="gray60" variant={Type.body1}>
          {formatDisplayTimestamp(metadata.duration / 1000, this.props.intl)}
        </StyledType>
      </GridContainerMetadata>
    );
  }

  render() {
    const { type, title } = this.props;

    return (
      <div data-qa="header-with-breadcrumbs">
        <FlexContainer style={{ '--margin-bottom': tokens.spacer32 }}>
          {this.renderArtwork()}
          <GridContainer>
            <Type as="p" condensed color="gray60" variant={Type.cta3}>
              <FormattedMessage id={`header.attribution.type-${type}`} defaultMessage={`${type}`} />
            </Type>
            <ResizableText>
              <Type as="h1" condensed variant={Type.heading1} id="title">
                {title}
              </Type>
            </ResizableText>
            <div>
              {this.renderAttribution()}
              {this.renderMetadata()}
            </div>
            {this.props.type === 'podcast' && (
              <ButtonPrimary
                style={{ justifySelf: `start` }}
                buttonSize={ButtonPrimary.sm}
                theme={{
                  ...colorThemeGenerator({
                    primaryColor: tokens.azure,
                    primaryColorText: tokens.white,
                    primaryColorFocusText: tokens.white,
                    primaryColorActiveText: tokens.white,
                  }),
                }}
                onClick={
                  this.props.toggleTrailerSelectionModal
                    ? this.props.toggleTrailerSelectionModal
                    : /* istanbul ignore next */ () => {}
                }
              >
                {this.props.trailer ? (
                  <FormattedMessage
                    id={`header.button.manage-trailer}`}
                    defaultMessage="Manage Trailer"
                  />
                ) : (
                  <FormattedMessage
                    id={`header.button.set-trailer}`}
                    defaultMessage="Set Trailer"
                  />
                )}
              </ButtonPrimary>
            )}
          </GridContainer>
        </FlexContainer>
        {this.renderMetrics()}
      </div>
    );
  }
}

// Styled Type
const StyledType = styled(Type)`
  font-size: 14px;

  a[href]:not([class]) {
    text-decoration: none;
  }
`;

// Styled Img
const Artwork = styled.img`
  display: block;
  flex-shrink: 0;
  height: 220px;
  margin-right: ${tokens.spacer24};
  width: 220px;

  @media (max-width: 480px) {
    height: 100%;
    width: 100%;
  }
`;

// Flex Container
const FlexContainer = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: var(--margin-bottom, 0);

  @media (max-width: 480px) {
    margin-bottom: 0;
    flex-direction: column;
    align-items: start;
    margin-top: 24px;
  }
`;

// Grid Container
const GridContainer = styled.div`
  display: grid;
  grid-template-rows: 30px min-content auto 32px;
  height: 220px;

  p:first-of-type {
    align-self: end;
    color: black;
    font-weight: 900;
  }

  @media (max-width: 480px) {
    margin-bottom: 0;
    flex-direction: column;
    height: initial;
  }
`;

const GridContainerMetadata = styled(GridContainer)`
  align-items: center;
  grid-gap: 5px;
  grid-template-rows: unset;
  grid-template-columns: repeat(3, max-content);
  height: initial;

  p:first-of-type {
    align-self: initial;
    font-weight: 400;
  }
`;
